import { FC, memo, useMemo } from 'react';
import { Button } from '@mui/material';
import { AxiosError } from 'axios';
import { ReactComponent as TrashService } from 'assets/icons/trash.svg';
import { EditIconButton } from 'components/elements';
import toastService from 'components/elements/Toastify';
import ConfirmationModal from 'components/modules/ConfirmationModal';
import { ICityAliasByProviderItem, ICityAliasFormValues, IErrorResponse } from 'types';
import { Rights } from 'utils/enums/rights.enum';
import { getCityOptionTitle, getErrorMessage } from 'utils/helpers';
import { useModal } from 'utils/hooks';
import { usePermissions } from 'utils/hooks/use-permissions';
import { CityAliasesByProviderService } from 'utils/services';
import { ManageCityAliasModal } from '../ManageCityAliasModal';

const DEVICE_FP = '6037e65eefbdasdfadsfasdf3';

interface UpdateCityAliasAnchorProps {
	row: ICityAliasByProviderItem;
	cityOptions: { title: string; value: string }[];
	cityOptionsLoading: boolean;
	onCitySearch: (search: string) => void;
	onCityLoadMore: () => void;
	hasMoreCityOptions: boolean;
	providerOptions: { title: string; value: string }[];
	onSuccess: () => Promise<void>;
}

export const UpdateCityAliasAnchor: FC<UpdateCityAliasAnchorProps> = memo(
	({ row, cityOptions, cityOptionsLoading, onCitySearch, onCityLoadMore, hasMoreCityOptions, providerOptions, onSuccess }) => {
		const [isOpen, handleOpen, handleClose] = useModal();
		const [isDeleteModalOpen, handleOpenDeleteModal, handleCloseDeleteModal] = useModal();
		const checkRights = usePermissions();

		const initialValues = useMemo(
			() => ({
				name: row.name,
				cityId: row.cityId ? `${row.cityId}` : '',
				providerId: `${row.providerId}`,
			}),
			[row]
		);

		const canDelete = checkRights(Rights.FULL_ACCESS);

		const handleUpdate = async (values: ICityAliasFormValues) => {
			try {
				await CityAliasesByProviderService.updateCityAliasByProvider({
					id: row.id,
					name: values.name.trim(),
					cityId: Number(values.cityId),
					providerId: Number(values.providerId),
					deviceFP: DEVICE_FP,
				});
				toastService.success('Аліас міста успішно оновлено');
				handleClose();
				await onSuccess();
			} catch (error) {
				toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
			}
		};

		const handleDelete = async () => {
			try {
				await CityAliasesByProviderService.deleteCityAliasByProvider({
					id: row.id,
					deviceFP: DEVICE_FP,
				});
				toastService.success('Аліас міста успішно видалено');
				handleCloseDeleteModal();
				await onSuccess();
			} catch (error) {
				toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
			}
		};

		return (
			<>
				<EditIconButton onClick={handleOpen} />
				<ManageCityAliasModal
					title="Оновити аліас міста"
					isOpen={isOpen}
					onClose={handleClose}
					initialValues={initialValues}
					onSubmit={handleUpdate}
					buttonText="Оновити"
					cityOptions={cityOptions}
					cityOptionsLoading={cityOptionsLoading}
					onCitySearch={onCitySearch}
					onCityLoadMore={onCityLoadMore}
					hasMoreCityOptions={hasMoreCityOptions}
					initialCityTitle={row.city ? getCityOptionTitle(row.city.id, row.city.name) : ''}
					providerOptions={providerOptions}
					canDelete={canDelete}
					onDeleteRequest={() => {
						handleClose();
						handleOpenDeleteModal();
					}}
				/>
				<ConfirmationModal
					open={isDeleteModalOpen}
					onClose={handleCloseDeleteModal}
					title={`Видалити аліас ${row.name}?`}
					message="Після видалення аліас не можна буде відновити."
					modalIcon={<TrashService />}
				>
					<div style={{ display: 'flex', gap: '10px' }}>
						<Button onClick={handleCloseDeleteModal} style={{ borderRadius: '5px' }} variant="contained" color="primary">
							Скасувати
						</Button>
						<Button onClick={handleDelete} style={{ borderRadius: '5px' }} variant="contained" color="secondary">
							Видалити
						</Button>
					</div>
				</ConfirmationModal>
			</>
		);
	}
);
